import { NATIONAL_CLIENT_IDENTIFIER_TYPE } from '@1trading/ts-common/nci';
import type { DocDefinitions } from '@sumsub/websdk/types/types';

import { NONE_VALUE } from '@/constants/answers';
import i18n from '@/lib/i18n';
import type { CountriesRecord } from '@/services/api/mainstream';
import type { NationalClientIdentifier } from '@/services/api/profile/national-client-identifier';
import type { ValueOf } from '@/types/generics';

export const NationalClientIdentifierType = NATIONAL_CLIENT_IDENTIFIER_TYPE;
export type NationalClientIdentifierType = ValueOf<typeof NationalClientIdentifierType>;

export enum NationalClientIdentifierJustificationReason {
  EXPIRED = 'EXPIRED',
  LOST = 'LOST',
  NOT_IN_POSSESSION = 'NOT_IN_POSSESSION',
  RENEWING = 'RENEWING',
}

export const getNationalClientIdentifierDocumentDefinitions = (nationalClientIdentifier: NationalClientIdentifier, countries: CountriesRecord): DocDefinitions | null => {
  const country = countries[nationalClientIdentifier.answers.country]?.alpha3;
  if (!country) return null;

  if (nationalClientIdentifier.answers.identifierType === NationalClientIdentifierType.CCPT) {
    return {
      IDENTITY: {
        country,
        idDocType: 'PASSPORT',
      },
    };
  }

  return null;
};

export const getNationalClientIdentifierTypeForCountry = (country: string, countries: CountriesRecord): NationalClientIdentifierType[] => countries[country]?.nciPriority ?? [];

interface GetNationalClientIdentifierTypeLabelOptions {
  short?: boolean;
}

export const getNationalClientIdentifierTypeLabel = (
  identifierType: NationalClientIdentifierType | typeof NONE_VALUE,
  country: string,
  options?: GetNationalClientIdentifierTypeLabelOptions,
) => {
  const { short = false } = options ?? {};

  switch (identifierType) {
    case NONE_VALUE:
    case NationalClientIdentifierType.CONCAT:
      return short ? i18n.t('fundamentals.none') : i18n.t('fundamentals.noneOfTheAbove');
    case NationalClientIdentifierType.NIDN: {
      if (i18n.exists(`nationalClientIdentifier.type.options.${identifierType}.countryDescriptions.${country}`))
        return i18n.t(`nationalClientIdentifier.type.options.${identifierType}.countryDescriptions.${country}`);

      return i18n.t(`nationalClientIdentifier.type.options.${identifierType}.title`);
    }
    default:
      return i18n.t(`nationalClientIdentifier.type.options.${identifierType}.title`);
  }
};

export const appendNationalClientIdentifierExample = (text: string, example: string | undefined) =>
  example ? i18n.t('nationalClientIdentifier.withExample', { example, text }) : text;

export const getPriorityCountryForCitizenships = (citizenship: string, citizenship2: string | undefined, countries: CountriesRecord): string | undefined => {
  if (!citizenship2) return citizenship;

  const eea1 = countries[citizenship]?.eea;
  const eea2 = countries[citizenship2]?.eea;
  if (eea1 && !eea2) return citizenship;
  if (!eea1 && eea2) return citizenship2;

  // Both EEA or both non-EEA: use alphabetically first
  return [citizenship, citizenship2].filter(Boolean).sort((a, b) => a.localeCompare(b))[0];
};
